import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CircleGeometry,
  type Color,
  CylinderGeometry,
  LatheGeometry,
  Mesh,
  RingGeometry,
  SphereGeometry,
  TorusGeometry,
  Vector2,
  Vector3,
} from 'three';
import type { HelmetKind, WheelKind } from '../../config/eras';
import { GeoBuilder } from '../geo';
import { toonMaterial } from '../toon';
import { type CarColors, type CarKit, M, segment, type Side, SIDES, twoSided } from './kit';

/** Parts every era shares: wheels, the big-headed driver and his scarf. */

function tireGeometry(r: number, w: number, rin: number, bev: number, segs = 18): BufferGeometry {
  const pts = [
    new Vector2(rin, -w / 2),
    new Vector2(r - bev, -w / 2),
    new Vector2(r, -w / 2 + bev),
    new Vector2(r, w / 2 - bev),
    new Vector2(r - bev, w / 2),
    new Vector2(rin, w / 2),
  ];
  const g = new LatheGeometry(pts, segs);
  g.rotateX(Math.PI / 2); // axle along z
  return g;
}

/** One wheel (tyre, rim and the marks that make its spin read), axle along z, `s` = which side faces out. */
export function wheelGeometry(kind: WheelKind, r: number, w: number, s: Side, c: CarColors): BufferGeometry {
  const g = new GeoBuilder();
  const axial = (rTop: number, rBot: number, h: number, color: Color, segs = 12, z = 0): void => {
    g.add(new CylinderGeometry(rTop, rBot, h, segs), color, M(0, 0, z, (s * Math.PI) / 2, 0, 0));
  };
  const faceDisc = (radius: number, x: number, y: number, z: number, color: Color, segs = 10): void => {
    g.add(new CircleGeometry(radius, segs), color, M(x, y, z, 0, s < 0 ? Math.PI : 0, 0));
  };
  switch (kind) {
    case 'wire': {
      // Tall, skinny tyre on a laced wire wheel with a brass knock-off.
      const rin = r * 0.76;
      g.add(tireGeometry(r, w, rin, Math.min(0.06, w * 0.3)), c.tire);
      for (const zz of [-1, 1]) g.add(new TorusGeometry(rin, 0.028, 4, 24), c.bright, M(0, 0, zz * w * 0.3));
      axial(r * 0.15, r * 0.17, w * 0.9, c.metal, 10);
      const n = 14;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const lace = i % 2 ? 1 : -1;
        const a2 = a + 0.3 * lace;
        const { len, m } = segment(
          new Vector3(Math.cos(a) * r * 0.15, Math.sin(a) * r * 0.15, lace * w * 0.36),
          new Vector3(Math.cos(a2) * rin, Math.sin(a2) * rin, lace * w * 0.1),
        );
        g.add(new CylinderGeometry(0.014, 0.014, len, 4), c.bright, m);
      }
      const zo = s * (w * 0.45 + 0.03);
      axial(0.05, 0.065, 0.07, c.brass, 8, zo);
      g.add(new BoxGeometry(0.25, 0.045, 0.03), c.brass, M(0, 0, zo + s * 0.03));
      break;
    }
    case 'disc': {
      // Streamliner discs: a smooth cover with a few cut-outs and a painted centre.
      const rin = r * 0.64;
      g.add(tireGeometry(r, w, rin, Math.min(0.07, w * 0.28)), c.tire);
      axial(rin, rin, w * 0.66, c.disc, 20);
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + 0.5;
        faceDisc(r * 0.075, Math.cos(a) * rin * 0.64, Math.sin(a) * rin * 0.64, s * (w * 0.33 + 0.004), c.dark, 8);
      }
      axial(r * 0.19, r * 0.16, w * 0.8, c.accent, 10);
      g.add(new BoxGeometry(0.2, 0.04, 0.03), c.metal, M(0, 0, s * (w * 0.4 + 0.02)));
      break;
    }
    case 'classic': {
      // The 1961 car's chunky tyres, as on the car sheet.
      g.add(tireGeometry(r, w, r * 0.45, Math.min(0.07, w * 0.22)), c.tire);
      axial(r * 0.46, r * 0.46, w * 0.5, c.metal, 12);
      faceDisc(r * 0.22, 0, 0, s * (w / 2 + 0.005), c.dark, 10);
      for (let k = 0; k < 3; k++) g.add(new CylinderGeometry(0.02, 0.02, r * 0.8, 4), c.dark, M(0, 0, s * w * 0.26, 0, 0, (k / 3) * Math.PI));
      break;
    }
    case 'grooved': {
      // 2000s grooved tyres: four rings round the tread, dark forged rims.
      const rin = r * 0.6;
      g.add(tireGeometry(r, w, rin, Math.min(0.09, w * 0.2), 20), c.tire);
      for (const f of [-0.3, -0.1, 0.1, 0.3]) g.add(new CylinderGeometry(r + 0.004, r + 0.004, 0.02, 24, 1, true), c.groove, M(0, 0, f * w, Math.PI / 2, 0, 0));
      axial(rin, rin, w * 0.78, c.rim, 18);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        faceDisc(rin * 0.2, Math.cos(a) * rin * 0.58, Math.sin(a) * rin * 0.58, s * (w * 0.39 + 0.004), c.dark, 8);
      }
      axial(r * 0.1, r * 0.12, w * 0.86, c.metal, 8);
      break;
    }
    case 'covered': {
      // 2020s: big rims and low-profile tyres behind flat wheel covers.
      const rin = r * 0.74;
      g.add(tireGeometry(r, w, rin, Math.min(0.08, w * 0.16), 22), c.tire);
      axial(rin, rin, w * 0.8, c.rim, 22);
      g.add(new RingGeometry(rin * 0.6, rin * 0.72, 24), c.main, M(0, 0, s * (w * 0.4 + 0.004), 0, s < 0 ? Math.PI : 0, 0));
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        faceDisc(rin * 0.1, Math.cos(a) * rin * 0.36, Math.sin(a) * rin * 0.36, s * (w * 0.4 + 0.005), c.main, 8);
      }
      axial(r * 0.08, r * 0.1, w * 0.9, c.metal, 8);
      break;
    }
    case 'slick': {
      // Fat slicks on painted five-hole rims.
      const rin = r * 0.6;
      g.add(tireGeometry(r, w, rin, Math.min(0.1, w * 0.2), 20), c.tire);
      axial(rin, rin, w * 0.78, c.rim, 18);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        faceDisc(rin * 0.26, Math.cos(a) * rin * 0.56, Math.sin(a) * rin * 0.56, s * (w * 0.39 + 0.004), c.dark, 10);
      }
      axial(r * 0.1, r * 0.12, w * 0.86, c.metal, 8);
      break;
    }
  }
  return g.build();
}

/**
 * Parts that steer with a front wheel but don't spin with it: the little
 * winglet hovering over a 2020s front tyre. Null for every other kind.
 */
export function uprightGeometry(kind: WheelKind, r: number, w: number, s: Side, c: CarColors): BufferGeometry | null {
  if (kind !== 'covered') return null;
  const g = new GeoBuilder();
  // A lip hugging the upper front of the tyre (theta round the axle, from just above the front).
  const lip = new CylinderGeometry(r + 0.035, r + 0.035, w * 0.7, 8, 1, true, Math.PI / 2 + 0.25, 0.85);
  g.add(twoSided(lip), c.accent, M(0, 0, -s * w * 0.1, Math.PI / 2, 0, 0));
  return g.build();
}

/** The big caricature head in its own frame (it bobbles on a spring). */
export function headGeometry(kind: HelmetKind, c: CarColors): BufferGeometry {
  const g = new GeoBuilder();
  const goggles = (frame: Color): void => {
    for (const s of SIDES) {
      g.add(new CylinderGeometry(0.13, 0.14, 0.12, 14), frame, M(0.3, 0.17, s * 0.145, 0, 0, Math.PI / 2));
      g.add(new CircleGeometry(0.105, 16), c.lens, M(0.365, 0.17, s * 0.145, 0, Math.PI / 2, 0));
    }
  };
  const chin = (): void => {
    g.add(new SphereGeometry(0.2, 10, 8), c.skin, M(0.17, 0.02, 0), true);
  };
  if (kind === 'cloth') {
    g.add(new SphereGeometry(0.36, 16, 12), c.helmet, M(0, 0.2, 0), true);
    g.add(new TorusGeometry(0.36, 0.045, 6, 22), c.brown, M(0, 0.17, 0, Math.PI / 2, 0, 0.12));
    chin();
    goggles(c.brown);
  } else if (kind === 'leather') {
    // Soft leather cap with a crown seam and ear flaps; steel-rimmed goggles.
    const shape = M(0, 0.21, 0, 0, 0, 0, 1.02, 0.94, 1);
    g.add(new SphereGeometry(0.35, 16, 12), c.leather, shape, true);
    g.add(new TorusGeometry(0.338, 0.022, 4, 20, Math.PI), c.brown, shape);
    for (const s of SIDES) g.add(new SphereGeometry(0.14, 8, 6), c.leather, M(-0.03, 0.05, s * 0.29, 0, 0, 0, 0.8, 1.25, 0.5), true);
    chin();
    goggles(c.metal);
  } else {
    // Full-face: a dark visor slot and a painted stripe over the crown, no face.
    g.add(new SphereGeometry(0.37, 18, 14), c.helmet, M(0, 0.2, 0), true);
    g.add(new SphereGeometry(0.376, 16, 4, Math.PI - 0.9, 1.8, 1.36, 0.52), c.visor, M(0, 0.2, 0), true);
    g.add(new SphereGeometry(0.374, 24, 2, Math.PI + 0.62, Math.PI - 0.4, Math.PI / 2 - 0.13, 0.26), c.stripe, M(0, 0.2, 0, Math.PI / 2, 0, 0), true);
  }
  return g.build();
}

export interface DriverSpec {
  /** Torso centre. */
  x: number;
  y: number;
  /** Recline in radians: 0 sits bolt upright (vintage), ~0.5 lies back (modern). */
  lean: number;
  /** Steering wheel centre, radius and rake. */
  wx: number;
  wy: number;
  wr: number;
  rake: number;
}

/** Torso, arms and steering wheel go into the body mesh; returns where the head and scarf attach. */
export function addDriver(k: CarKit, d: DriverSpec, scarf: boolean): { headBase: Vector3; scarfRoot: Vector3 | null } {
  const c = k.c;
  const cl = Math.cos(d.lean);
  const sl = Math.sin(d.lean);
  const at = (ox: number, oy: number, oz = 0): Vector3 => new Vector3(d.x + ox * cl - oy * sl, d.y + ox * sl + oy * cl, oz);
  k.add(new CylinderGeometry(0.2, 0.27, 0.52, 9), c.suit, M(d.x, d.y, 0, 0, 0, d.lean));
  const sh = at(-0.01, 0.12);
  k.add(new SphereGeometry(0.2, 8, 6), c.suit, M(sh.x, sh.y, 0, 0, 0, d.lean, 0.9, 0.62, 1.55));
  k.add(new TorusGeometry(d.wr, 0.028, 5, 14), c.brown, M(d.wx, d.wy, 0, 0, Math.PI / 2, d.rake, 1, 1, 1, 'ZYX'));
  for (const s of SIDES) {
    const hand = new Vector3(d.wx - 0.03, d.wy + 0.02, s * d.wr * 0.85);
    k.rod(at(0.02, 0.1, s * 0.2), hand, 0.068, c.suit, 6);
    k.add(new SphereGeometry(0.08, 6, 5), c.brown, M(hand.x, hand.y, hand.z));
  }
  let scarfRoot: Vector3 | null = null;
  if (scarf) {
    scarfRoot = at(-0.1, 0.16, 0.1);
    const knot = at(-0.08, 0.14, 0.06);
    k.add(new SphereGeometry(0.1, 6, 5), c.scarf, M(knot.x, knot.y, knot.z));
  }
  return { headBase: at(0, 0.19), scarfRoot };
}

/** The scarf: a ribbon that hangs at rest and streams back at speed. */
export class Scarf {
  readonly mesh: Mesh;
  private geo: BufferGeometry;
  private pos: Float32Array;
  private readonly segs = 10;
  private t = 0;

  constructor(
    color: Color,
    private root: Vector3,
    private length = 1.25,
    private width = 0.16,
  ) {
    this.geo = new BufferGeometry();
    this.pos = new Float32Array((this.segs + 1) * 2 * 3);
    const idx: number[] = [];
    for (let i = 0; i < this.segs; i++) {
      const a = i * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    this.geo.setIndex(idx);
    this.geo.setAttribute('position', new BufferAttribute(this.pos, 3));
    this.mesh = new Mesh(this.geo, toonMaterial(color, { doubleSide: true }));
    this.mesh.castShadow = true;
    this.mesh.frustumCulled = false;
    this.update(0, 0);
  }

  /** speed in m/s; builds the flapping ribbon in the chassis frame. */
  update(dt: number, speed: number): void {
    this.t += dt * (6 + Math.min(speed, 60) * 0.22);
    const s = Math.min(1, speed / 25);
    const segLen = this.length / this.segs;
    let x = this.root.x;
    let y = this.root.y;
    let z = this.root.z;
    for (let i = 0; i <= this.segs; i++) {
      const f = i / this.segs;
      const flap = Math.sin(this.t - i * 0.85) * (0.04 + 0.13 * s) * f;
      const sway = Math.cos(this.t * 0.7 - i * 0.6) * (0.04 + 0.14 * s) * f;
      if (i > 0) {
        // Streams back and off to the driver's left, nearly level at speed, like the poster.
        const back = 0.3 + 0.35 * s;
        x -= segLen * back;
        y += -segLen * (1 - s) * 0.85 - segLen * 0.06 * s + flap * 0.2;
        z += -segLen * 0.9 * s + sway * 0.35;
      }
      const w = this.width * (1 - f * 0.25);
      const k = i * 6;
      this.pos[k] = x;
      this.pos[k + 1] = y + w / 2 + flap;
      this.pos[k + 2] = z + sway * 0.3;
      this.pos[k + 3] = x;
      this.pos[k + 4] = y - w / 2 + flap;
      this.pos[k + 5] = z - sway * 0.3;
    }
    this.geo.getAttribute('position').needsUpdate = true;
    this.geo.computeVertexNormals();
  }
}
